import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";

export default function WordRedefine({
    open,
    onClose,
    onSave,
    word,
    definition,
    hiddenMessage
}) {
    const [editingDefinition, setEditingDefinition] = useState("");
    const [saveAsHiddenMessage, setSaveAsHiddenMessage] = useState(false);

    // Update editingDefinition when definition prop changes
    useEffect(() => {
        setEditingDefinition(definition || "");
    }, [definition]);

    // Check if word matches existing hidden message and set checkbox accordingly
    useEffect(() => {
        if (word && hiddenMessage) {
            const wordUpper = word.toUpperCase();
            const hiddenMessageUpper = hiddenMessage.toUpperCase();
            setSaveAsHiddenMessage(wordUpper === hiddenMessageUpper);
        } else {
            setSaveAsHiddenMessage(false);
        }
    }, [word, hiddenMessage]);

    const handleSave = () => {
        if (onSave && editingDefinition.trim()) {
            onSave(editingDefinition.trim(), saveAsHiddenMessage);
        }
    };

    const handleCancel = () => {
        if (onClose) {
            onClose();
        }
    };

    // Determine if this is for a grid puzzle (word search or hidden message)
    const isGridPuzzle = !!hiddenMessage;

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                Edit Puzzle Title
            </DialogTitle>
            <DialogContent>
                {isGridPuzzle ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="h6">
                            Important Note:
                        </Typography>
                        <Typography>
                            The word "{word}" will be the HIDDEN MESSAGE to this puzzle, and this definition will be used as the puzzle's title/clue.
                        </Typography>
                        <Typography>
                            Use the + button and ASSOC menu to automatically place letters. Letters will be randomly placed in the grid for the HIDDEN MESSAGE puzzle.
                        </Typography>
                        <Typography sx={{ mt: 1, fontWeight: 'medium' }}>
                            Note: If the system already has this hidden message or title, it will cause an error.
                        </Typography>
                    </Alert>
                ) : (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="h6">
                            Important Note:
                        </Typography>
                        <Typography>
                            This clue/definition will be used for the word "{word}" in the crossword puzzle. Make sure the clue is clear and appropriate for the difficulty level.
                        </Typography>
                        <Typography sx={{ mt: 1, fontWeight: 'medium' }}>
                            Note: The clue should help solvers find the answer without being too obvious or too obscure.
                        </Typography>
                    </Alert>
                )}
                <TextField
                    autoFocus
                    margin="dense"
                    label={isGridPuzzle ? "Title / Definition" : "Clue / Definition"}
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    value={editingDefinition}
                    onChange={(e) => setEditingDefinition(e.target.value)}
                    sx={{ mt: 2 }}
                />
                
                {isGridPuzzle && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={saveAsHiddenMessage}
                                onChange={(e) => setSaveAsHiddenMessage(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={`Save "${word}" as hidden message`}
                        sx={{ mt: 2 }}
                    />
                )}

            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
                <Button onClick={handleCancel} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={!editingDefinition.trim()}
                >
                    Save Clue
                </Button>
            </DialogActions>
        </Dialog>
    );
}

